import type { Role } from "api/roles/Role.typed";
import type { UserWithRole } from "api/auth/Auth.typed";
import type { QualificationItem } from "api/qualification/Qualification.typed";
import { Avatar, Box, Option, Stack, Tooltip } from "@mui/joy";
import DarkSelect from "components/common/DarkSelect";
import DarkTypography from "components/common/DarkTypography";
import DarkInput from "components/common/DarkInput";
import DarkCheckbox from "components/common/DarkCheckbox";
import { useAuth } from "contexts/api";
import { Permission, usePermissions } from "contexts/app/permissions";
import { useUsersModuleData } from "contexts/app/usersModuleData";
import { SClose, SDelete, SEdit, SDone, SFile, SMailOutline } from "styles/StyledMUI";
import React from "react";
import { useConfirmationModal } from "contexts/app/confirmation";
import UserDocumentsSection from "./UserDocumentsSection";
import IconButtonCTA from "components/common/IconButtonCTA";
import {
  colorTokens,
  gradientListRowCardShellDirectorySx,
  gradientListRowCardShellSx,
  listRowIconButtonPlainSx,
  textTokens,
  withAlpha,
} from "styles/globalStyles";

type UserItemProps = {
  userWithRole: UserWithRole;
  refreshUsers: () => void;
  qualificationItems?: QualificationItem[];
  autoOpenDocuments?: boolean;
};

function initialsFromName(first?: string, last?: string): string {
  const a = (first?.trim()?.[0] ?? "").toUpperCase();
  const b = (last?.trim()?.[0] ?? "").toUpperCase();
  const s = a + b;

  return s || "?";
}

/** Shown next to the name when encadrant / guide is enabled. */
const GUIDE_MEDAL_EMOJI = "🏅";

export default function UserItem({
  userWithRole,
  refreshUsers,
  qualificationItems = [],
  autoOpenDocuments = false,
}: UserItemProps) {
  const {
    updateUserRole,
    updateUserProfile,
    deleteUser,
    activateUserAccount,
    resendUserInvitation,
    user: authUser,
  } = useAuth();
  const { hasPermission } = usePermissions();
  const { permissionsCatalog } = useUsersModuleData();
  const isSelf = authUser != null && authUser.id === userWithRole.user.id;
  const canEditProfile =
    hasPermission(Permission.USERS_ALL_EDIT) || hasPermission(Permission.USERS_COMPANY_EDIT);
  const canDeleteUser =
    hasPermission(Permission.USERS_ALL_EDIT) || hasPermission(Permission.USERS_COMPANY_EDIT);
  const canEditRole = hasPermission(Permission.USERS_ROLES_EDIT);
  const canEditDocuments =
    hasPermission(Permission.USERS_DOCUMENTS_EDIT) ||
    (hasPermission(Permission.USERS_OWN_EDIT) && isSelf);
  const modalContext = useConfirmationModal();

  const roleId = userWithRole.role?.id ?? null;
  const roleName = userWithRole.role?.name ?? "";

  const [role, setRole] = React.useState<number | null>(roleId);
  const [lastQualification, setLastQualification] = React.useState<string>(
    userWithRole.user.last_qualification ?? "",
  );
  const [isEditingProfile, setIsEditingProfile] = React.useState(false);
  const [editFirstName, setEditFirstName] = React.useState(userWithRole.user.first_name ?? "");
  const [editLastName, setEditLastName] = React.useState(userWithRole.user.last_name ?? "");
  const [editLastQualification, setEditLastQualification] = React.useState(
    userWithRole.user.last_qualification ?? "",
  );
  const [editPhoneNumber, setEditPhoneNumber] = React.useState(
    userWithRole.user.phone_number ?? "",
  );
  const [editIsGuide, setEditIsGuide] = React.useState(Boolean(userWithRole.user.is_guide));
  const [isOpenDocuments, setIsOpenDocuments] = React.useState(false);
  const [editExternalId, setEditExternalId] = React.useState(userWithRole.user.external_id ?? "");
  const [activateEmail, setActivateEmail] = React.useState("");
  const [activateRole, setActivateRole] = React.useState<number | null>(null);
  const rolesFromCatalog: Role[] | undefined = permissionsCatalog?.roles;
  const canLogin = userWithRole.user.can_login !== false;
  const pendingInvite = !canLogin && Boolean(userWithRole.user.email?.trim());
  const directoryOnly = !canLogin && !pendingInvite;

  React.useEffect(() => {
    if (autoOpenDocuments && canEditDocuments) {
      setIsOpenDocuments(true);
    }
  }, [autoOpenDocuments, canEditDocuments]);

  React.useEffect(() => {
    if (!canEditDocuments) {
      setIsOpenDocuments(false);
    }
  }, [canEditDocuments]);

  React.useEffect(() => {
    const id = userWithRole.role?.id ?? null;
    setRole(id);
    setLastQualification(userWithRole.user.last_qualification ?? "");
    setEditFirstName(userWithRole.user.first_name ?? "");
    setEditLastName(userWithRole.user.last_name ?? "");
    setEditExternalId(userWithRole.user.external_id ?? "");
    setEditLastQualification(userWithRole.user.last_qualification ?? "");
    setEditPhoneNumber(userWithRole.user.phone_number ?? "");
    setEditIsGuide(Boolean(userWithRole.user.is_guide));
  }, [
    userWithRole.role?.id,
    userWithRole.user.first_name,
    userWithRole.user.last_name,
    userWithRole.user.last_qualification,
    userWithRole.user.external_id,
    userWithRole.user.phone_number,
    userWithRole.user.is_guide,
  ]);

  const handleRoleChange = async (newRole: number | null) => {
    if (newRole === null || newRole === role) return;
    setRole(newRole);
    await updateUserRole(userWithRole.user.id, newRole);
    refreshUsers();
  };

  const displayRoleName = React.useMemo(() => {
    if (role == null) return null;
    const found = rolesFromCatalog?.find((r) => Number(r.id) === Number(role));
    return found?.name ?? roleName ?? "—";
  }, [role, rolesFromCatalog, roleName]);

  const qualificationDisplayLabel = React.useMemo(() => {
    if (!lastQualification?.trim()) {
      return "—";
    }
    const q = qualificationItems.find((item) => item.sigle === lastQualification);

    if (q?.name) {
      return `${q.sigle} – ${q.name}`;
    }

    return q?.sigle ?? lastQualification;
  }, [lastQualification, qualificationItems]);

  React.useEffect(() => {
    if (!canEditProfile) {
      setIsEditingProfile(false);
      setEditFirstName(userWithRole.user.first_name ?? "");
      setEditLastName(userWithRole.user.last_name ?? "");
      setEditExternalId(userWithRole.user.external_id ?? "");
      setEditLastQualification(userWithRole.user.last_qualification ?? "");
      setEditPhoneNumber(userWithRole.user.phone_number ?? "");
      setEditIsGuide(Boolean(userWithRole.user.is_guide));
    }
  }, [
    canEditProfile,
    userWithRole.user.first_name,
    userWithRole.user.last_name,
    userWithRole.user.external_id,
    userWithRole.user.last_qualification,
    userWithRole.user.phone_number,
    userWithRole.user.is_guide,
  ]);

  const inputCompactSx = {
    fontSize: "0.875rem",
    backgroundColor: colorTokens.bg.surfaceMuted,
    color: colorTokens.text.primary,
    border: `1px solid ${colorTokens.border.default}`,
    "&:hover": { borderColor: colorTokens.border.strong },
    "&:focus-within": { borderColor: colorTokens.border.focus },
  };

  const lockedValueBoxSx = {
    color: textTokens.color.primary,
    fontWeight: 400,
    lineHeight: 1.3,
    fontSize: "0.8rem",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  };

  const fieldLabelBoxSx = {
    color: textTokens.color.subtle,
    fontWeight: 600,
    mb: 0.3,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    fontSize: "0.65rem",
  };

  const beginProfileEdit = () => {
    setEditFirstName(userWithRole.user.first_name ?? "");
    setEditLastName(userWithRole.user.last_name ?? "");
    setEditExternalId(userWithRole.user.external_id ?? "");
    setEditLastQualification(lastQualification);
    setEditPhoneNumber(userWithRole.user.phone_number ?? "");
    setEditIsGuide(Boolean(userWithRole.user.is_guide));
    setActivateEmail("");
    setActivateRole(rolesFromCatalog?.[0]?.id ?? null);
    setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    const email = activateEmail.trim().toLowerCase();
    const converting = directoryOnly && email !== "";

    if (converting) {
      if (!/.+@.+\..+/.test(email)) {
        return;
      }

      if (canEditRole && activateRole === null) {
        return;
      }

      const ok = await modalContext.showConfirmation("Créer un compte", "Envoyer l'invitation ?");
      if (!ok) return;
    }

    await updateUserProfile(userWithRole.user.id, {
      first_name: editFirstName.trim(),
      last_name: editLastName.trim(),
      last_qualification: editLastQualification,
      external_id: editExternalId.trim(),
      phone_number: editPhoneNumber.trim(),
      is_guide: editIsGuide,
    });

    if (converting) {
      await activateUserAccount(userWithRole.user.id, {
        email,
        role_id: canEditRole ? (activateRole ?? undefined) : undefined,
      });
    }

    setLastQualification(editLastQualification);
    setIsEditingProfile(false);
    refreshUsers();
  };

  const handleCancelProfile = () => {
    setEditFirstName(userWithRole.user.first_name ?? "");
    setEditLastName(userWithRole.user.last_name ?? "");
    setEditExternalId(userWithRole.user.external_id ?? "");
    setEditLastQualification(userWithRole.user.last_qualification ?? "");
    setEditPhoneNumber(userWithRole.user.phone_number ?? "");
    setEditIsGuide(Boolean(userWithRole.user.is_guide));
    setActivateEmail("");
    setActivateRole(null);
    setLastQualification(userWithRole.user.last_qualification ?? "");
    setIsEditingProfile(false);
  };

  const handleDelete = async () => {
    const confirm = await modalContext.showConfirmation(
      "Confirmation",
      "Voulez-vous supprimer cet utilisateur ?",
    );

    if (confirm) {
      await deleteUser(userWithRole.user.id);
      refreshUsers();
    }
  };

  const handleResendInvitation = async () => {
    const ok = await modalContext.showConfirmation(
      "Renvoyer l'invitation",
      "Renvoyer l'e-mail d'invitation ?",
    );
    if (!ok) return;

    await resendUserInvitation(userWithRole.user.id);
  };

  const showQualificationCol = qualificationItems.length > 0 || Boolean(lastQualification?.trim());

  const displayFullName = [
    userWithRole.user.first_name?.trim(),
    userWithRole.user.last_name?.trim(),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const showGuideLeadEmoji =
    isEditingProfile && canEditProfile ? editIsGuide : Boolean(userWithRole.user.is_guide);

  const detailsGridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(2, minmax(0, 1fr))",
      md: "repeat(3, minmax(0, 1fr))",
      lg: "repeat(auto-fit, minmax(148px, 1fr))",
    },
    gap: 1,
    alignItems: "start",
  };

  return (
    <Box sx={canLogin ? gradientListRowCardShellSx : gradientListRowCardShellDirectorySx}>
      <Box sx={{ p: 1 }}>
        {/* Ligne 1 : identité + actions (style EquipmentCards) */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
            <Avatar
              sx={{
                display: { xs: "none", sm: "flex" },
                width: 40,
                height: 40,
                flexShrink: 0,
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: colorTokens.accent.primary,
                fontWeight: 700,
                fontSize: "0.9rem",
                border: `1px solid ${colorTokens.border.default}`,
              }}
              aria-hidden
            >
              {initialsFromName(
                isEditingProfile && canEditProfile ? editFirstName : userWithRole.user.first_name,
                isEditingProfile && canEditProfile ? editLastName : userWithRole.user.last_name,
              )}
            </Avatar>
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
              {isEditingProfile && canEditProfile ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    alignItems: "center",
                  }}
                >
                  {showGuideLeadEmoji ? (
                    <Box
                      component="span"
                      role="img"
                      aria-label="Encadrant"
                      sx={{ fontSize: "1.15rem", lineHeight: 1, flexShrink: 0 }}
                    >
                      {GUIDE_MEDAL_EMOJI}
                    </Box>
                  ) : null}
                  <DarkInput
                    placeholder="Prénom"
                    size="sm"
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    sx={{ ...inputCompactSx, flex: "1 1 100px", minWidth: 88, maxWidth: 160 }}
                  />
                  <DarkInput
                    placeholder="Nom"
                    size="sm"
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    sx={{ ...inputCompactSx, flex: "1 1 100px", minWidth: 88, maxWidth: 160 }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                  }}
                >
                  {showGuideLeadEmoji ? (
                    <Box
                      component="span"
                      role="img"
                      aria-label="Encadrant"
                      sx={{ fontSize: "1.15rem", lineHeight: 1, flexShrink: 0 }}
                    >
                      {GUIDE_MEDAL_EMOJI}
                    </Box>
                  ) : null}
                  <DarkTypography
                    level="title-md"
                    colorVariant="primary"
                    sx={{
                      fontWeight: 700,
                      minWidth: 0,
                      flex: 1,
                      overflow: "hidden",
                      display: { xs: "-webkit-box", sm: "block" },
                      WebkitBoxOrient: { xs: "vertical" },
                      WebkitLineClamp: { xs: 2 },
                      wordBreak: { xs: "break-word" },
                      whiteSpace: { xs: "normal", sm: "nowrap" },
                      textOverflow: { sm: "ellipsis" },
                      lineHeight: { xs: 1.25, sm: 1.2 },
                    }}
                  >
                    {displayFullName || "—"}
                  </DarkTypography>
                </Box>
              )}
              <DarkTypography
                level="body-xs"
                colorVariant="muted"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {userWithRole.user.email?.trim()
                  ? userWithRole.user.email
                  : canLogin
                    ? "—"
                    : "Sans compte"}
              </DarkTypography>
            </Stack>
          </Box>

          <Box sx={{ display: "flex", gap: 0.3, flexShrink: 0, alignItems: "center", ml: "auto" }}>
            {canEditProfile && pendingInvite && !isEditingProfile ? (
              <Tooltip title="Renvoyer l'invitation">
                <IconButtonCTA
                  ctaVariant="accent"
                  size="sm"
                  variant="plain"
                  onClick={handleResendInvitation}
                  aria-label="Renvoyer l'invitation"
                  sx={{
                    ...listRowIconButtonPlainSx,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.accent.primary, 0.1) },
                  }}
                >
                  <SMailOutline />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
            {canEditProfile && !isEditingProfile ? (
              <Tooltip title="Modifier le profil">
                <IconButtonCTA
                  ctaVariant="accent"
                  size="sm"
                  variant="plain"
                  onClick={beginProfileEdit}
                  aria-label="Modifier le profil"
                  sx={{
                    ...listRowIconButtonPlainSx,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.accent.primary, 0.1) },
                  }}
                >
                  <SEdit />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
            {canEditProfile && isEditingProfile ? (
              <>
                <Tooltip title="Enregistrer le profil">
                  <IconButtonCTA
                    ctaVariant="success"
                    size="sm"
                    variant="plain"
                    onClick={handleSaveProfile}
                    aria-label="Enregistrer le profil"
                    sx={{
                      ...listRowIconButtonPlainSx,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
                    }}
                  >
                    <SDone sx={{ color: colorTokens.status.success }} />
                  </IconButtonCTA>
                </Tooltip>
                <Tooltip title="Annuler">
                  <IconButtonCTA
                    ctaVariant="danger"
                    size="sm"
                    variant="plain"
                    onClick={handleCancelProfile}
                    aria-label="Annuler la modification du profil"
                    sx={{
                      ...listRowIconButtonPlainSx,
                      "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                    }}
                  >
                    <SClose />
                  </IconButtonCTA>
                </Tooltip>
              </>
            ) : null}
            {canEditDocuments ? (
              <Tooltip title={isOpenDocuments ? "Masquer les documents" : "Documents"}>
                <IconButtonCTA
                  ctaVariant="neutral"
                  size="sm"
                  variant="plain"
                  onClick={() => setIsOpenDocuments((prev) => !prev)}
                  aria-label="Documents utilisateur"
                  aria-expanded={isOpenDocuments}
                  sx={listRowIconButtonPlainSx}
                >
                  <SFile />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
            {canDeleteUser ? (
              <Tooltip title="Supprimer l'utilisateur">
                <IconButtonCTA
                  ctaVariant="danger"
                  size="sm"
                  variant="plain"
                  onClick={handleDelete}
                  aria-label="Supprimer l'utilisateur"
                  sx={{
                    ...listRowIconButtonPlainSx,
                    "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
                  }}
                >
                  <SDelete />
                </IconButtonCTA>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        {/* Ligne 2 : grille détails (style attributs EquipmentCards) */}
        <Box sx={detailsGridSx}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Identifiant externe
              </DarkTypography>
            </Box>
            {isEditingProfile && canEditProfile ? (
              <DarkInput
                placeholder="ID externe"
                size="sm"
                value={editExternalId}
                onChange={(e) => setEditExternalId(e.target.value)}
                aria-label="Identifiant externe"
                sx={{ ...inputCompactSx, width: "100%" }}
              />
            ) : (
              <Box sx={lockedValueBoxSx}>
                {userWithRole.user.external_id?.trim() ? userWithRole.user.external_id : "—"}
              </Box>
            )}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                Téléphone
              </DarkTypography>
            </Box>
            {isEditingProfile && canEditProfile ? (
              <DarkInput
                placeholder="Téléphone"
                size="sm"
                value={editPhoneNumber}
                onChange={(e) => setEditPhoneNumber(e.target.value)}
                aria-label="Numéro de téléphone"
                sx={{ ...inputCompactSx, width: "100%" }}
              />
            ) : (
              <Box sx={lockedValueBoxSx}>
                {userWithRole.user.phone_number?.trim() ? userWithRole.user.phone_number : "—"}
              </Box>
            )}
          </Box>

          {directoryOnly && isEditingProfile && canEditProfile ? (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={fieldLabelBoxSx}>
                <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                  Email
                </DarkTypography>
              </Box>
              <DarkInput
                placeholder="Email"
                size="sm"
                value={activateEmail}
                onChange={(e) => setActivateEmail(e.target.value.toLowerCase())}
                aria-label="Email du compte"
                sx={{ ...inputCompactSx, width: "100%" }}
              />
            </Box>
          ) : null}

          {showQualificationCol ? (
            <Box sx={{ minWidth: 0, gridColumn: { xs: "1 / -1", md: "span 1" } }}>
              <Box sx={fieldLabelBoxSx}>
                <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                  Qualification
                </DarkTypography>
              </Box>
              {canEditProfile && isEditingProfile ? (
                <DarkSelect
                  value={editLastQualification || null}
                  onChange={(e, newValue) =>
                    setEditLastQualification((newValue as string | null) ?? "")
                  }
                  size="sm"
                  placeholder="—"
                >
                  <Option value="">—</Option>
                  {qualificationItems.map((q) => (
                    <Option key={q.id} value={q.sigle}>
                      {q.name ? `${q.sigle} – ${q.name}` : q.sigle}
                    </Option>
                  ))}
                </DarkSelect>
              ) : (
                <Box sx={lockedValueBoxSx}>{qualificationDisplayLabel}</Box>
              )}
            </Box>
          ) : null}

          <Box
            sx={{
              minWidth: 0,
              gridColumn: { xs: "1 / -1", lg: "span 2" },
              maxWidth: { lg: 420 },
            }}
          >
            <Box sx={fieldLabelBoxSx}>
              <DarkTypography level="body-xs" colorVariant="muted" sx={{ fontWeight: 600 }}>
                {canLogin || pendingInvite || (directoryOnly && isEditingProfile && canEditRole)
                  ? "Rôle"
                  : "Encadrant"}
              </DarkTypography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                width: "100%",
                minWidth: 0,
              }}
            >
              {canLogin || pendingInvite ? (
                <Box sx={{ flex: "1 1 160px", minWidth: 0, maxWidth: { lg: 360 } }}>
                  {canEditRole ? (
                    <DarkSelect
                      value={role}
                      onChange={(e, newValue) => handleRoleChange(newValue as number | null)}
                      size="sm"
                      placeholder="—"
                      renderValue={() => (role != null ? (displayRoleName ?? "—") : "—")}
                    >
                      {rolesFromCatalog && rolesFromCatalog.length > 0 ? (
                        rolesFromCatalog.map((r) => (
                          <Option key={r.id} value={Number(r.id)} label={r.name}>
                            {r.name}
                          </Option>
                        ))
                      ) : (
                        <DarkTypography level="body-sm">Aucun rôle disponible</DarkTypography>
                      )}
                    </DarkSelect>
                  ) : (
                    <Box sx={lockedValueBoxSx}>{displayRoleName ?? "—"}</Box>
                  )}
                </Box>
              ) : null}
              {directoryOnly && isEditingProfile && canEditProfile && canEditRole ? (
                <Box sx={{ flex: "1 1 160px", minWidth: 0, maxWidth: { lg: 360 } }}>
                  <DarkSelect
                    placeholder="Rôle"
                    value={activateRole}
                    onChange={(_, newValue) => setActivateRole((newValue as number) ?? null)}
                    size="sm"
                    disabled={!rolesFromCatalog || rolesFromCatalog.length === 0}
                    aria-label="Rôle du compte"
                  >
                    {rolesFromCatalog?.map((r) => (
                      <Option key={r.id} value={r.id}>
                        {r.name}
                      </Option>
                    ))}
                  </DarkSelect>
                </Box>
              ) : null}
              {isEditingProfile && canEditProfile ? (
                <DarkCheckbox
                  checked={editIsGuide}
                  onChange={(e) => setEditIsGuide(e.target.checked)}
                  label="Encadrant"
                  size="sm"
                  sx={{ flexShrink: 0 }}
                  aria-label="Encadrant"
                />
              ) : directoryOnly ? (
                <Box sx={lockedValueBoxSx}>{userWithRole.user.is_guide ? "Oui" : "Non"}</Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>

      {isOpenDocuments && canEditDocuments && (
        <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, px: 1, pb: 1, pt: 1.5 }}>
          <UserDocumentsSection key={userWithRole.user.id} userId={userWithRole.user.id} canEdit />
        </Box>
      )}
    </Box>
  );
}
